package com.dotdrives.client

import com.dotdrives.core.uuidV7
import com.dotdrives.model.ActivityEvent
import com.dotdrives.model.Drive
import com.dotdrives.model.DriveFile
import com.dotdrives.model.DriveFinancials
import com.dotdrives.model.DrivePhase
import com.dotdrives.model.DrivePhaseTask
import com.dotdrives.model.DrivePhaseTemplate
import com.dotdrives.model.DriveSummary
import com.dotdrives.model.DriveTaskItem
import com.dotdrives.model.NewDrive
import com.dotdrives.model.Target
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import com.dotdrives.model.DriveSnapshotRequest
import com.dotdrives.model.DriveBoardSnapshot
import io.github.jan.supabase.postgrest.query.Count
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.FilterOperator
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.time.Clock
import kotlin.time.Instant

data class DrivesPage(
    val items: List<DriveSummary>,
    val totalCount: Long,
)

class DriveClient(
    private val client: SupabaseClient = SupabaseProvider.client,
    private val authClient: AuthClient = AuthClient(client),
) {
    suspend fun snapshot(orgId: String, request: DriveSnapshotRequest): DriveBoardSnapshot =
        client.postgrest.rpc("drive_board_snapshot", buildJsonObject {
            put("p_org", orgId); put("p_template", request.templateId)
            request.query?.let { put("p_query", it) }
            request.stewardUserId?.let { put("p_steward", it) }
            request.targetId?.let { put("p_target", it) }
            request.tag?.let { put("p_tag", it) }
            request.giverType?.let { put("p_giver_type", it) }
            request.phaseId?.let { put("p_phase", it) }
            put("p_offset", request.offset); put("p_closed", request.closed)
            request.startDate?.let { put("p_start", it) }
            request.endDate?.let { put("p_end", it) }
        }).decodeAs<DriveBoardSnapshot>()

    suspend fun templates(orgId: String): List<DrivePhaseTemplate> =
        client.from(TEMPLATES_TABLE)
            .select {
                order("name", Order.ASCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("is_active", true)
                }
            }
            .decodeList()

    suspend fun phases(orgId: String, templateId: String): List<DrivePhase> =
        client.from(PHASES_TABLE)
            .select {
                order("sequence", Order.ASCENDING)
                order("id", Order.ASCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("template_id", templateId)
                }
            }
            .decodeList()

    suspend fun phaseTasks(orgId: String, phaseId: String): List<DrivePhaseTask> =
        client.from(PHASE_TASKS_TABLE)
            .select {
                order("sequence", Order.ASCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("phase_id", phaseId)
                }
            }
            .decodeList()

    suspend fun targets(orgId: String): List<Target> =
        client.from(TARGETS_TABLE)
            .select {
                order("name", Order.ASCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("is_active", true)
                }
            }
            .decodeList()

    suspend fun listActive(
        orgId: String,
        templateId: String,
        query: String?,
        stewardUserId: String?,
        targetId: String?,
        tag: String?,
        giverType: String?,
    ): List<DriveSummary> {
        val rows = fetchAllPages(POSTGREST_PAGE_SIZE) { offset ->
            client.from(DRIVES_TABLE)
                .select {
                    range(offset, offset + POSTGREST_PAGE_SIZE - 1)
                    order("title", Order.ASCENDING)
                    order("id", Order.ASCENDING)
                    filter {
                        eq("org_id", orgId)
                        eq("template_id", templateId)
                        filter("outcome", FilterOperator.IS, null)
                        filter("archived_at", FilterOperator.IS, null)
                        query?.trim()?.takeIf(String::isNotEmpty)?.let { ilike("title", "%$it%") }
                        stewardUserId?.let { eq("steward_user_id", it) }
                        targetId?.let { eq("target_id", it) }
                        tag?.let { contains("tags", listOf(it)) }
                    }
                }
                .decodeList<DriveRow>()
        }
        return enrich(orgId, rows, giverType)
    }

    suspend fun listCompleted(orgId: String, page: Int, pageSize: Int, templateId: String? = null): DrivesPage {
        require(page >= 1) { "page must be at least 1" }
        require(pageSize >= 1) { "pageSize must be at least 1" }
        val from = (page - 1L) * pageSize
        val to = from + pageSize - 1L
        val response = client.from(DRIVES_TABLE)
            .select {
                count(Count.EXACT)
                range(from, to)
                order("updated_at", Order.DESCENDING)
                filter {
                    eq("org_id", orgId)
                    templateId?.let { eq("template_id", it) }
                    filterNot("outcome", FilterOperator.IS, null)
                }
            }
        val rows = response.decodeList<DriveRow>()
        return DrivesPage(
            items = enrich(orgId, rows, giverType = null),
            totalCount = response.countOrNull() ?: rows.size.toLong(),
        )
    }

    suspend fun get(orgId: String, driveId: String): Drive {
        val row = client.from(DRIVES_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", driveId)
                }
            }
            .decodeSingle<DriveRow>()
        val giverNames = loadGiverNames(orgId, listOfNotNull(row.giverId, row.advocateGiverId))
        val stewardName = loadUserName(row.stewardUserId)
        val phaseName = loadPhaseName(orgId, row.currentPhaseId)
        val targetName = row.targetId?.let { loadTargetName(orgId, it) }
        return Drive(
            id = row.id,
            orgId = row.orgId,
            title = row.title,
            giverId = row.giverId,
            giverDisplayName = giverNames[row.giverId] ?: row.title,
            advocateGiverId = row.advocateGiverId,
            advocateGiverName = row.advocateGiverId?.let(giverNames::get),
            stewardUserId = row.stewardUserId,
            stewardName = stewardName,
            templateId = row.templateId,
            currentPhaseId = row.currentPhaseId,
            currentPhaseName = phaseName,
            phaseEnteredAt = row.phaseEnteredAt,
            targetId = row.targetId,
            targetName = targetName,
            targetText = row.targetText,
            askAmount = row.askAmount,
            outcome = row.outcome,
            tags = row.tags,
            archivedAt = row.archivedAt,
            createdAt = row.createdAt,
            updatedAt = row.updatedAt,
        )
    }

    suspend fun create(orgId: String, drive: NewDrive): Drive {
        // The server still owns phase_entered_at and defaults omitted phases to the
        // first phase. Explicit column creation must seed tasks from that same phase.
        val phases = phases(orgId, drive.templateId)
        val startingPhase = drive.initialPhaseId?.let { selectedId ->
            phases.firstOrNull { it.id == selectedId }
                ?: error("The selected phase is no longer in this process. Choose a process and try again.")
        } ?: phases.minWithOrNull(compareBy<DrivePhase> { it.sequence }.thenBy { it.id })
            ?: error("Process template has no phases")
        val id = uuidV7()
        client.from(DRIVES_TABLE).insert(
            DriveInsert(
                id = id,
                orgId = orgId,
                title = drive.title,
                giverId = drive.giverId,
                advocateGiverId = drive.advocateGiverId,
                stewardUserId = drive.stewardUserId,
                templateId = drive.templateId,
                targetId = drive.targetId,
                targetText = drive.targetText,
                askAmount = drive.askAmount,
                currentPhaseId = drive.initialPhaseId,
            ),
        )
        seedPhaseTasks(orgId, id, startingPhase.id)
        return get(orgId, id)
    }

    suspend fun movePhase(
        orgId: String,
        driveId: String,
        fromPhaseId: String,
        fromPhaseName: String,
        toPhaseId: String,
        toPhaseName: String,
        outcome: String?,
    ) {
        val actor = authClient.requireCurrentUserId()
        client.from(ACTIVITY_EVENTS_TABLE).insert(
            ActivityEventInsert(
                id = uuidV7(),
                orgId = orgId,
                actorUserId = actor,
                verb = DRIVE_PHASE_CHANGED_VERB,
                subjectType = DRIVE_SUBJECT_TYPE,
                subjectId = driveId,
                payload = buildJsonObject {
                    put("from_phase_id", fromPhaseId)
                    put("from_phase_name", fromPhaseName)
                    put("to_phase_id", toPhaseId)
                    put("to_phase_name", toPhaseName)
                    put("changed_by", actor)
                },
            ),
        )
        if (outcome != null) {
            client.from(DRIVES_TABLE).update(
                DriveOutcomeUpdate(outcome = outcome),
            ) {
                filter {
                    eq("org_id", orgId)
                    eq("id", driveId)
                }
            }
        }
        seedPhaseTasks(orgId, driveId, toPhaseId)
    }

    suspend fun tasks(orgId: String, driveId: String): List<DriveTaskItem> {
        val instances = fetchAllPages(POSTGREST_PAGE_SIZE) { offset ->
            client.from(DRIVE_TASKS_TABLE)
                .select {
                    range(offset, offset + POSTGREST_PAGE_SIZE - 1)
                    order("id", Order.ASCENDING)
                    filter {
                        eq("org_id", orgId)
                        eq("drive_id", driveId)
                    }
                }
                .decodeList<DriveTaskRow>()
        }
        if (instances.isEmpty()) return emptyList()
        val phaseTaskIds = instances.map(DriveTaskRow::phaseTaskId).distinct()
        val definitions = phaseTaskIds.chunked(ID_BATCH_SIZE).flatMap { ids ->
            client.from(PHASE_TASKS_TABLE)
                .select {
                    filter {
                        eq("org_id", orgId)
                        isIn("id", ids)
                    }
                }
                .decodeList<DrivePhaseTask>()
        }
            .associateBy(DrivePhaseTask::id)
        return instances.mapNotNull { instance ->
            val definition = definitions[instance.phaseTaskId] ?: return@mapNotNull null
            DriveTaskItem(
                id = instance.id,
                driveId = instance.driveId,
                phaseTaskId = instance.phaseTaskId,
                name = definition.name,
                category = definition.category,
                sequence = definition.sequence,
                isComplete = instance.isComplete,
                completedBy = instance.completedBy,
                completedAt = instance.completedAt,
            )
        }.sortedBy(DriveTaskItem::sequence)
    }

    suspend fun setTaskComplete(
        orgId: String,
        driveId: String,
        taskId: String,
        isComplete: Boolean,
    ): DriveTaskItem {
        val updated = client.from(DRIVE_TASKS_TABLE)
            .update(
                DriveTaskUpdate(
                    isComplete = isComplete,
                    completedBy = if (isComplete) authClient.requireCurrentUserId() else null,
                    completedAt = if (isComplete) Clock.System.now() else null,
                ),
            ) {
                select()
                filter {
                    eq("org_id", orgId)
                    eq("id", taskId)
                    eq("drive_id", driveId)
                }
            }
            .decodeSingle<DriveTaskRow>()
        val definition = client.from(PHASE_TASKS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", updated.phaseTaskId)
                }
            }
            .decodeSingle<DrivePhaseTask>()
        return DriveTaskItem(
            id = updated.id,
            driveId = updated.driveId,
            phaseTaskId = updated.phaseTaskId,
            name = definition.name,
            category = definition.category,
            sequence = definition.sequence,
            isComplete = updated.isComplete,
            completedBy = updated.completedBy,
            completedAt = updated.completedAt,
        )
    }

    suspend fun financials(orgId: String, driveIds: List<String>): Map<String, DriveFinancials> {
        if (driveIds.isEmpty()) return emptyMap()
        return driveIds.distinct().chunked(ID_BATCH_SIZE).flatMap { ids ->
            client.from(DRIVE_FINANCIALS_TABLE)
                .select {
                    filter {
                        eq("org_id", orgId)
                        isIn("drive_id", ids)
                    }
                }
                .decodeList<DriveFinancialsRow>()
        }
            .associate { it.driveId to DriveFinancials(it.driveId, it.proposedAmount, it.pledgedAmount, it.receivedAmount) }
    }

    suspend fun files(orgId: String, driveId: String): List<DriveFile> =
        client.from(FILES_TABLE)
            .select {
                order("created_at", Order.DESCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("owner_type", DRIVE_OWNER_TYPE)
                    eq("owner_id", driveId)
                }
            }
            .decodeList<FileRow>()
            .map { DriveFile(it.id, it.fileName, it.fileSize, it.storagePath, it.uploadedBy, it.createdAt) }

    suspend fun activity(orgId: String, driveId: String): List<ActivityEvent> =
        client.from(ACTIVITY_EVENTS_TABLE)
            .select {
                order("occurred_at", Order.DESCENDING)
                limit(ACTIVITY_LIMIT)
                filter {
                    eq("org_id", orgId)
                    eq("subject_type", DRIVE_SUBJECT_TYPE)
                    eq("subject_id", driveId)
                }
            }
            .decodeList()

    private suspend fun seedPhaseTasks(orgId: String, driveId: String, phaseId: String) {
        val definitions = phaseTasks(orgId, phaseId)
        if (definitions.isEmpty()) return
        val existing = definitions.map(DrivePhaseTask::id).chunked(ID_BATCH_SIZE).flatMap { ids ->
            fetchAllPages(POSTGREST_PAGE_SIZE) { offset ->
                client.from(DRIVE_TASKS_TABLE)
                    .select {
                        range(offset, offset + POSTGREST_PAGE_SIZE - 1)
                        order("id", Order.ASCENDING)
                        filter {
                            eq("org_id", orgId)
                            eq("drive_id", driveId)
                            isIn("phase_task_id", ids)
                        }
                    }
                    .decodeList<DriveTaskRow>()
            }
        }
            .map(DriveTaskRow::phaseTaskId)
            .toSet()
        val missing = definitions.filterNot { it.id in existing }
        if (missing.isEmpty()) return
        client.from(DRIVE_TASKS_TABLE).insert(
            missing.map { task ->
                DriveTaskInsert(id = uuidV7(), orgId = orgId, driveId = driveId, phaseTaskId = task.id)
            },
        )
    }

    private suspend fun enrich(
        orgId: String,
        rows: List<DriveRow>,
        giverType: String?,
    ): List<DriveSummary> {
        if (rows.isEmpty()) return emptyList()
        val giverIds = rows.map(DriveRow::giverId).distinct()
        val givers = loadGivers(orgId, giverIds)
        val stewardNames = loadUserNames(rows.mapNotNull(DriveRow::stewardUserId).distinct())
        val targetNames = loadTargetNames(orgId, rows.mapNotNull(DriveRow::targetId).distinct())
        val taskCounts = loadTaskCounts(orgId, rows.map(DriveRow::id))
        return rows.mapNotNull { row ->
            val giver = givers[row.giverId]
            if (giverType != null && giver?.giverType != giverType) return@mapNotNull null
            val counts = taskCounts[row.id] ?: (0 to 0)
            DriveSummary(
                id = row.id,
                title = row.title,
                giverId = row.giverId,
                giverDisplayName = giver?.displayName ?: row.title,
                giverType = giver?.giverType ?: "individual",
                currentPhaseId = row.currentPhaseId,
                targetId = row.targetId,
                targetName = row.targetId?.let(targetNames::get),
                targetText = row.targetText,
                tags = row.tags,
                askAmount = row.askAmount,
                stewardUserId = row.stewardUserId,
                stewardName = stewardNames[row.stewardUserId],
                phaseEnteredAt = row.phaseEnteredAt,
                tasksComplete = counts.first,
                tasksTotal = counts.second,
                outcome = row.outcome,
                updatedAt = row.updatedAt,
            )
        }
    }

    private suspend fun loadGivers(orgId: String, giverIds: List<String>): Map<String, GiverLookupRow> {
        if (giverIds.isEmpty()) return emptyMap()
        return giverIds.distinct().chunked(ID_BATCH_SIZE).flatMap { ids ->
            client.from(GIVERS_TABLE)
                .select {
                    filter {
                        eq("org_id", orgId)
                        isIn("id", ids)
                    }
                }
                .decodeList<GiverLookupRow>()
        }
            .associateBy(GiverLookupRow::id)
    }

    private suspend fun loadGiverNames(orgId: String, giverIds: List<String>): Map<String, String> =
        loadGivers(orgId, giverIds).mapValues { it.value.displayName }

    private suspend fun loadUserNames(userIds: List<String>): Map<String, String> {
        if (userIds.isEmpty()) return emptyMap()
        return userIds.distinct().chunked(ID_BATCH_SIZE).flatMap { ids ->
            client.from(PROFILES_TABLE)
                .select {
                    filter {
                        isIn("user_id", ids)
                    }
                }
                .decodeList<ProfileRow>()
        }
            .associate { profile ->
                profile.userId to listOf(profile.firstName, profile.lastName)
                    .filter(String::isNotBlank)
                    .joinToString(" ")
            }
    }

    private suspend fun loadUserName(userId: String): String? = loadUserNames(listOf(userId))[userId]

    private suspend fun loadTargetNames(orgId: String, targetIds: List<String>): Map<String, String> {
        if (targetIds.isEmpty()) return emptyMap()
        return targetIds.distinct().chunked(ID_BATCH_SIZE).flatMap { ids ->
            client.from(TARGETS_TABLE)
                .select {
                    filter {
                        eq("org_id", orgId)
                        isIn("id", ids)
                    }
                }
                .decodeList<TargetNameRow>()
        }
            .associate { it.id to it.name }
    }

    private suspend fun loadTargetName(orgId: String, targetId: String): String? =
        loadTargetNames(orgId, listOf(targetId))[targetId]

    private suspend fun loadPhaseName(orgId: String, phaseId: String): String? =
        client.from(PHASES_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", phaseId)
                }
            }
            .decodeList<PhaseNameRow>()
            .firstOrNull()
            ?.name

    private suspend fun loadTaskCounts(orgId: String, driveIds: List<String>): Map<String, Pair<Int, Int>> {
        if (driveIds.isEmpty()) return emptyMap()
        val rows = driveIds.distinct().chunked(ID_BATCH_SIZE).flatMap { ids ->
            fetchAllPages(POSTGREST_PAGE_SIZE) { offset ->
                client.from(DRIVE_TASKS_TABLE)
                    .select {
                        range(offset, offset + POSTGREST_PAGE_SIZE - 1)
                        order("drive_id", Order.ASCENDING)
                        order("id", Order.ASCENDING)
                        filter {
                            eq("org_id", orgId)
                            isIn("drive_id", ids)
                        }
                    }
                    .decodeList<TaskCountRow>()
            }
        }
        return rows.groupBy(TaskCountRow::driveId).mapValues { (_, tasks) ->
            tasks.count(TaskCountRow::isComplete) to tasks.size
        }
    }
}

@Serializable
private data class DriveRow(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val title: String,
    @SerialName("giver_id")
    val giverId: String,
    @SerialName("advocate_giver_id")
    val advocateGiverId: String?,
    @SerialName("steward_user_id")
    val stewardUserId: String,
    @SerialName("template_id")
    val templateId: String,
    @SerialName("current_phase_id")
    val currentPhaseId: String,
    @SerialName("phase_entered_at")
    val phaseEnteredAt: Instant,
    @SerialName("target_id")
    val targetId: String?,
    @SerialName("target_text")
    val targetText: String?,
    @SerialName("ask_amount")
    val askAmount: Double?,
    val outcome: String?,
    val tags: List<String>,
    @SerialName("archived_at")
    val archivedAt: Instant?,
    @SerialName("created_at")
    val createdAt: Instant,
    @SerialName("updated_at")
    val updatedAt: Instant,
)

@Serializable
private data class DriveInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val title: String,
    @SerialName("giver_id")
    val giverId: String,
    @SerialName("advocate_giver_id")
    val advocateGiverId: String?,
    @SerialName("steward_user_id")
    val stewardUserId: String,
    @SerialName("template_id")
    val templateId: String,
    @SerialName("target_id")
    val targetId: String?,
    @SerialName("target_text")
    val targetText: String?,
    @SerialName("ask_amount")
    val askAmount: Double?,
    @SerialName("current_phase_id")
    val currentPhaseId: String? = null,
)

@Serializable
private data class DriveOutcomeUpdate(
    val outcome: String,
)

@Serializable
private data class DriveTaskRow(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("drive_id")
    val driveId: String,
    @SerialName("phase_task_id")
    val phaseTaskId: String,
    @SerialName("is_complete")
    val isComplete: Boolean,
    @SerialName("completed_by")
    val completedBy: String?,
    @SerialName("completed_at")
    val completedAt: Instant?,
)

@Serializable
private data class DriveTaskInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("drive_id")
    val driveId: String,
    @SerialName("phase_task_id")
    val phaseTaskId: String,
)

@Serializable
private data class DriveTaskUpdate(
    @SerialName("is_complete")
    val isComplete: Boolean,
    @SerialName("completed_by")
    val completedBy: String?,
    @SerialName("completed_at")
    val completedAt: Instant?,
)

@Serializable
private data class DriveFinancialsRow(
    @SerialName("drive_id")
    val driveId: String,
    @SerialName("proposed_amount")
    val proposedAmount: Double,
    @SerialName("pledged_amount")
    val pledgedAmount: Double,
    @SerialName("received_amount")
    val receivedAmount: Double,
)

@Serializable
private data class GiverLookupRow(
    val id: String,
    @SerialName("display_name")
    val displayName: String,
    @SerialName("giver_type")
    val giverType: String,
)

@Serializable
private data class ProfileRow(
    @SerialName("user_id")
    val userId: String,
    @SerialName("first_name")
    val firstName: String,
    @SerialName("last_name")
    val lastName: String,
)

@Serializable
private data class TargetNameRow(
    val id: String,
    val name: String,
)

@Serializable
private data class PhaseNameRow(
    val id: String,
    val name: String,
)

@Serializable
private data class TaskCountRow(
    @SerialName("drive_id")
    val driveId: String,
    @SerialName("is_complete")
    val isComplete: Boolean,
)

@Serializable
private data class FileRow(
    val id: String,
    @SerialName("file_name")
    val fileName: String,
    @SerialName("file_size")
    val fileSize: Long,
    @SerialName("storage_path")
    val storagePath: String,
    @SerialName("uploaded_by")
    val uploadedBy: String,
    @SerialName("created_at")
    val createdAt: Instant,
)

@Serializable
private data class ActivityEventInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("actor_user_id")
    val actorUserId: String?,
    val verb: String,
    @SerialName("subject_type")
    val subjectType: String,
    @SerialName("subject_id")
    val subjectId: String,
    val payload: kotlinx.serialization.json.JsonObject,
)

private const val DRIVES_TABLE = "drives"
private const val DRIVE_TASKS_TABLE = "drive_tasks"
private const val PHASES_TABLE = "drive_phases"
private const val PHASE_TASKS_TABLE = "drive_phase_tasks"
private const val TEMPLATES_TABLE = "drive_phase_templates"
private const val TARGETS_TABLE = "targets"
private const val DRIVE_FINANCIALS_TABLE = "drive_financials"
private const val ID_BATCH_SIZE = 100
private const val POSTGREST_PAGE_SIZE = 1_000L
private const val FILES_TABLE = "files"
private const val GIVERS_TABLE = "givers"
private const val PROFILES_TABLE = "profiles"
private const val ACTIVITY_EVENTS_TABLE = "activity_events"
private const val DRIVE_SUBJECT_TYPE = "drive"
private const val DRIVE_OWNER_TYPE = "drive"
private const val DRIVE_PHASE_CHANGED_VERB = "drive.phase_changed"
private const val ACTIVITY_LIMIT = 50L
